"use client"

import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import { Input, Spin, Empty, Pagination } from "antd"
import { motion } from "framer-motion"
import { apiRequest } from "../utils/apiRequest"
import { buildMeta, buildBlogCollectionJsonLd } from "../lib/seo"
import { siteBase } from "../components/Seo"
import { resolveMediaUrl } from "../utils/media"
import "./blog.css"

const PAGE_SIZE = 12

const BlogListPage = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState("")
  const meta = buildMeta({
    title: "Blog",
    description: "Latest posts, product updates, and announcements from Tanaka Life.",
    canonical: `${siteBase}/blog`,
  })

  useEffect(() => {
    let mounted = true
    setLoading(true)
    apiRequest({
      method: "get",
      url: `/blog?page=${page}&limit=${PAGE_SIZE}${q ? `&q=${encodeURIComponent(q)}` : ""}`,
    })
      .then((res) => {
        if (!mounted) return
        const body = res.data || res
        setItems(body.items || [])
        setTotal(body.pagination?.total || 0)
      })
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [page, q])

  return (
    <div className="blog-page">
      <Helmet>
        <title>{meta.title}</title>
        {meta.meta.map((m, i) => (
          <meta key={i} {...m} />
        ))}
        {meta.link.map((l, i) => (
          <link key={i} {...l} />
        ))}
        <script type="application/ld+json">{JSON.stringify(buildBlogCollectionJsonLd(siteBase))}</script>
      </Helmet>

      <header className="blog-list-hero">
        <h1>Blog</h1>
        <p>Updates, product news, and stories from the Tanaka Life team.</p>
      </header>

      <div className="blog-filters">
        <Input.Search
          placeholder="Search posts…"
          defaultValue={q}
          onSearch={(v) => { setQ(v); setPage(1) }}
          allowClear
        />
      </div>

      {/*
        Paging used to unmount the whole grid (and its own Pagination control)
        in favour of a bare spinner — a full layout collapse with no feedback
        near the click. Spin now overlays the existing cards, and Pagination
        stays mounted and disabled during the fetch instead of disappearing.
      */}
      <Spin spinning={loading} size="large" tip={items.length ? "Loading..." : undefined}>
        {items.length > 0 ? (
          <motion.div
            className="blog-grid"
            initial="hidden" animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
          >
            {items.map((a) => {
              let tags = []
              try { tags = a.tags_json ? JSON.parse(a.tags_json) : [] } catch {}
              return (
                <motion.div key={a.id}
                  variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
                >
                  <Link to={`/blog/${a.slug}`} className="blog-card">
                    {/*
                      The cover box is always rendered, even without an image,
                      so every card reserves the same aspect-ratio height —
                      previously a post with no cover_image_url skipped this
                      block entirely and its card came out shorter than its
                      siblings.
                    */}
                    <div className="cover">
                      {a.cover_image_url ? (
                        <img src={resolveMediaUrl(a.cover_image_url)} alt={a.cover_image_alt || a.title} loading="lazy" />
                      ) : (
                        <div className="cover-placeholder" aria-hidden="true">
                          {a.title?.trim()?.charAt(0)?.toUpperCase() || "T"}
                        </div>
                      )}
                    </div>
                    <div className="body">
                      <div>
                        {tags.slice(0, 3).map((t) => (
                          <span key={t} className="blog-tag-chip">{t}</span>
                        ))}
                      </div>
                      {/* Clamped so a long title/excerpt can't make one card
                          taller than its neighbours. Only the text itself is
                          clamped — never a wrapper around interactive
                          elements, which is what clipped the Buy Now button
                          on the product cards. This card has no button. */}
                      <h3 className="clamp-2">{a.title}</h3>
                      {/* The API guarantees a preview (excerpt, else subtitle,
                          else the opening body text), but a post whose body is
                          only an image can still have none. The paragraph is
                          rendered either way so the card keeps its reserved
                          height and stays aligned with its neighbours. */}
                      <p className="excerpt clamp-3">{a.excerpt || a.subtitle || ''}</p>
                      <div className="meta">
                        <span>{a.reading_minutes || 1} min read</span>
                        {a.published_at && <span>{new Date(a.published_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )
            })}
          </motion.div>
        ) : loading ? (
          <div style={{ minHeight: 300 }} />
        ) : (
          <Empty description="No posts yet — check back soon." />
        )}
      </Spin>

      {total > PAGE_SIZE && (
        <div style={{ textAlign: "center", marginTop: 36 }}>
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            onChange={setPage}
            showSizeChanger={false}
            disabled={loading}
          />
        </div>
      )}
    </div>
  )
}

export default BlogListPage
